<template>
	<div>
		<validation-observer ref="simpleRules">
			<form-wizard
				color="#296db4"
				:title="null"
				:subtitle="null"
				finish-button-text="Submit"
				back-button-text="Previous"
				skip-button-text="Skip"
				class="steps-transparent mb-3"
				@on-change="onWizardChange"
				@on-complete="addFarm()"
			>
				<!-- Farm Details -->
				<tab-content title="Farm Details" icon="feather icon-file-text">
					<b-row>
						<b-col cols="12" class="mb-2">
							<div class="d-flex">
								<feather-icon icon="ArrowLeftIcon" size="24" class="mr-2" @click="goBack" />
								<div>
									<h5 class="mb-0">Farm Details</h5>
									<small class="text-muted"> Enter Your Farm Details. </small>
								</div>
							</div>
						</b-col>

						<b-col md="6">
							<b-form-group label="Farm Name" label-for="farm_name">
								<validation-provider #default="{ errors }" name="Farm Name" rules="required">
									<b-form-input id="farm_name" name="farm_name" v-model="farmData.farm_name" />
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col cols="12" class="mb-2 mt-5">
							<h5 class="mb-0">Address Information</h5>
						</b-col>

						<b-col md="12">
							<b-form-group label="Address" label-for="address">
								<validation-provider #default="{ errors }" name="Address" rules="required">
									<b-form-textarea id="address" name="address" v-model="farmData.address" rows="3" />
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group label="Country" label-for="country">
								<v-select
									id="country"
									v-model="farmData.selectedCountry"
									:options="countryList"
									value-field="id"
									label="name"
									@input="getState"
								/>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group label="State" label-for="state">
								<v-select
									id="state"
									v-model="farmData.selectedState"
									:options="stateList"
									value-field="id"
									label="name"
									@input="getCity"
								/>
							</b-form-group>
						</b-col>

						<b-col md="6">
							<b-form-group label="City" label-for="city">
								<v-select id="city" v-model="farmData.selectedCity" :options="cityList" label="name" value-field="id" />
							</b-form-group>
						</b-col>
					</b-row>
				</tab-content>

				<!-- Farm Location -->
				<tab-content title="Farm Location" icon="feather icon-map-pin">
					<b-row>
						<b-col cols="12" class="mb-2">
							<h5 class="mb-0">Farm Location</h5>
							<small class="text-muted">
								Search for a location or click on map to pinpoint. Marker is
								draggable.
							</small>
						</b-col>

						<!-- Search (Nominatim / OpenStreetMap — free, no API key, no 403) -->
						<b-col cols="12" class="mb-1">
							<b-form-group label="Search location">
								<div class="nominatim-wrap">
									<b-form-input
										v-model="nominatimQuery"
										placeholder="Type an address or place name…"
										autocomplete="off"
										@input="onNominatimInput"
										@keydown.esc="nominatimResults = []"
									/>
									<div v-if="nominatimLoading" class="nominatim-status text-muted">Searching…</div>
									<ul v-else-if="nominatimResults.length" class="nominatim-list list-unstyled">
										<li
											v-for="r in nominatimResults"
											:key="r.place_id"
											class="nominatim-item"
											@mousedown.prevent="selectNominatimResult(r)"
										>
											{{ r.display_name }}
										</li>
									</ul>
								</div>
								<small class="text-muted">Type to search, pick a result — or click on map to pin directly.</small>
							</b-form-group>
						</b-col>

						<!-- Map -->
						<b-col cols="12" class="mb-2">
							<div class="map-wrap">
								<div ref="gmap" class="farm-map"></div>
							</div>

							<div v-if="mapError" class="text-danger mt-1">{{ mapError }}</div>
							<div v-else-if="!isMapReady" class="text-muted mt-1">Loading map...</div>
						</b-col>

						<!-- Lat/Lng -->
						<b-col md="4">
							<b-form-group label="Latitude">
								<validation-provider #default="{ errors }" name="Latitude" rules="required">
									<b-form-input :value="farmData.center_lat" readonly />
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="4">
							<b-form-group label="Longitude">
								<validation-provider #default="{ errors }" name="Longitude" rules="required">
									<b-form-input :value="farmData.center_lng" readonly />
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<b-col md="4">
							<b-form-group label="Allowed Distance (meters)">
								<b-form-input type="number" min="0" v-model.number="farmData.allowed_radius_m" />
								<small class="text-muted">QR scan blocked outside this radius.</small>
							</b-form-group>
						</b-col>

						<b-col cols="12" class="d-flex flex-wrap">
							<b-button size="sm" variant="outline-primary" class="mr-1 mb-1" @click="useMyLocation">
								Use my current location
							</b-button>
							<b-button size="sm" variant="outline-danger" class="mb-1" @click="clearPin">Clear Pin</b-button>
						</b-col>
					</b-row>
				</tab-content>
			</form-wizard>
		</validation-observer>
	</div>
</template>

<script>
import { FormWizard, TabContent } from "vue-form-wizard";
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import { heightTransition } from "@core/mixins/ui/transition";
import Ripple from "vue-ripple-directive";
import axiosIns from "@/libs/axios";
import shedUpdate from "@/libs/hatcheryUpdate";

import { GOOGLE_MAPS_KEY } from "@core/common/constants";

export default {
	components: {
		FormWizard,
		TabContent,
		vSelect,
		ValidationProvider,
		ValidationObserver,
	},
	directives: { Ripple },
	mixins: [heightTransition],
	data() {
		return {
			companyId: null,
			stateList: [],
			countryList: [],
			cityList: [],
			userList: [],
			companyRoles: [],

			// Map state
			mapError: null,
			isMapReady: false,
			isMapInitStarted: false,
			gmap: null,
			gmarker: null,

			// Nominatim search state
			nominatimQuery: '',
			nominatimResults: [],
			nominatimLoading: false,
			nominatimTimer: null,

			farmData: {
				farm_name: null,
				pin_code: null,
				address: null,
				selectedState: null,
				selectedCountry: null,
				selectedCity: null,

				center_lat: null,
				center_lng: null,
				allowed_radius_m: 150,

				assign_user: [{ role: null, name: null }],
			},
		};
	},
	mounted() {
		this.initTrHeight();
	},
	created() {
		this.companyId = this.$route.params.companyId;
		window.addEventListener("resize", this.initTrHeight);
		this.getCountry();
		this.getSelectedRole();
	},
	destroyed() {
		window.removeEventListener("resize", this.initTrHeight);
	},
	methods: {
		goBack() {
			this.$router.go(-1);
		},

		initTrHeight() {
			this.trSetHeight(null);
		},

		// vue-form-wizard emits (prevIndex, nextIndex)
		onWizardChange(prevIndex, nextIndex) {
			if (nextIndex === 1) {
				this.$nextTick(() => this.initGoogleMapOnce());
			}
		},

		// ---- Google Maps Geocoder search — uses already-loaded Maps JS API ----
		onNominatimInput() {
			clearTimeout(this.nominatimTimer);
			this.nominatimResults = [];
			const q = (this.nominatimQuery || '').trim();
			if (q.length < 3) return;
			this.nominatimLoading = true;
			this.nominatimTimer = setTimeout(async () => {
				try {
					if (!window.google || !window.google.maps) {
						await this.loadGoogleMapsScript();
					}
					const geocoder = new window.google.maps.Geocoder();
					geocoder.geocode({ address: q }, (results, status) => {
						if (status === 'OK' && results && results.length) {
							this.nominatimResults = results.map((r, i) => ({
								place_id: i,
								display_name: r.formatted_address,
								lat: r.geometry.location.lat(),
								lon: r.geometry.location.lng(),
							}));
						} else {
							this.nominatimResults = [];
						}
						this.nominatimLoading = false;
					});
				} catch {
					this.nominatimResults = [];
					this.nominatimLoading = false;
				}
			}, 400);
		},

		async selectNominatimResult(result) {
			this.nominatimQuery = result.display_name;
			this.nominatimResults = [];
			const lat = parseFloat(result.lat);
			const lng = parseFloat(result.lon);
			if (!this.isMapInitStarted) await this.initGoogleMapOnce();
			this.setPin(lat, lng, true);
		},

		// ---- Google Script Loader ----
		loadGoogleMapsScript() {
			return new Promise((resolve, reject) => {
				if (window.google && window.google.maps) {
					resolve();
					return;
				}

				const apiKey = GOOGLE_MAPS_KEY || process.env.VUE_APP_GOOGLE_MAPS_KEY;
				if (!apiKey) {
					reject(new Error("Google Maps key missing (VUE_APP_GOOGLE_MAPS_KEY)"));
					return;
				}

				const existing = document.querySelector('script[data-gmaps="1"]');
				if (existing) {
					const check = setInterval(() => {
						if (window.google && window.google.maps) {
							clearInterval(check);
							resolve();
						}
					}, 200);

					setTimeout(() => {
						clearInterval(check);
						reject(new Error("Google Maps load timeout (20s). Check referrer/API restrictions."));
					}, 20000);
					return;
				}

				const callbackName = "__initGMaps" + Date.now();
				window[callbackName] = () => {
					delete window[callbackName];
					resolve();
				};

				const script = document.createElement("script");
				script.setAttribute("data-gmaps", "1");
				script.async = true;
				script.defer = true;
				script.onerror = () => reject(new Error("Google Maps script load error"));

				// No &libraries=places needed — search is handled by Nominatim
				script.src =
					"https://maps.googleapis.com/maps/api/js" +
					`?key=${encodeURIComponent(apiKey)}` +
					"&v=weekly" +
					`&callback=${encodeURIComponent(callbackName)}`;

				document.head.appendChild(script);
			});
		},

		async initGoogleMapOnce() {
			if (this.isMapInitStarted) return;
			this.isMapInitStarted = true;

			try {
				await this.loadGoogleMapsScript();

				const startLat = this.farmData.center_lat || 20.5937;
				const startLng = this.farmData.center_lng || 78.9629;

				this.gmap = new window.google.maps.Map(this.$refs.gmap, {
					center: { lat: startLat, lng: startLng },
					zoom: this.farmData.center_lat ? 15 : 5,
					mapTypeControl: false,
					streetViewControl: false,
					fullscreenControl: false,
				});

				this.gmap.addListener("click", (e) => {
					this.setPin(e.latLng.lat(), e.latLng.lng(), true);
				});

				if (this.farmData.center_lat && this.farmData.center_lng) {
					this.setPin(this.farmData.center_lat, this.farmData.center_lng, false);
				}

				this.isMapReady = true;

				// Fix grey map after tab switch
				this.$nextTick(() => {
					if (this.gmap) window.google.maps.event.trigger(this.gmap, "resize");
				});
			} catch (err) {
				this.mapError =
					err && err.message
						? err.message
						: "Failed to load Google Maps. Check API key, billing, referrer restrictions, and enabled APIs.";
			}
		},


		setPin(lat, lng, pan = true) {
			const fixedLat = Number(Number(lat).toFixed(7));
			const fixedLng = Number(Number(lng).toFixed(7));

			this.farmData.center_lat = fixedLat;
			this.farmData.center_lng = fixedLng;

			if (!this.gmarker && this.gmap) {
				this.gmarker = new window.google.maps.Marker({
					position: { lat: fixedLat, lng: fixedLng },
					map: this.gmap,
					draggable: true,
				});

				this.gmarker.addListener("dragend", (e) => {
					this.setPin(e.latLng.lat(), e.latLng.lng(), false);
				});
			} else if (this.gmarker) {
				this.gmarker.setPosition({ lat: fixedLat, lng: fixedLng });
			}

			if (pan && this.gmap) {
				this.gmap.panTo({ lat: fixedLat, lng: fixedLng });
				if (this.gmap.getZoom() < 14) this.gmap.setZoom(14);
			}
		},

		clearPin() {
			this.farmData.center_lat = null;
			this.farmData.center_lng = null;
			if (this.gmarker) {
				this.gmarker.setMap(null);
				this.gmarker = null;
			}
		},

		useMyLocation() {
			if (!navigator.geolocation) {
				this.$toast({
					component: ToastificationContent,
					position: "top-right",
					props: { icon: "AlertTriangleIcon", variant: "warning", text: "Geolocation not supported in this browser." },
				});
				return;
			}

			navigator.geolocation.getCurrentPosition(
				async (pos) => {
					const lat = pos.coords.latitude;
					const lng = pos.coords.longitude;

					if (!this.isMapInitStarted) await this.initGoogleMapOnce();
					this.setPin(lat, lng, true);
				},
				() => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "AlertTriangleIcon",
							variant: "danger",
							text: "Unable to fetch current location. Please allow location permission.",
						},
					});
				},
				{ enableHighAccuracy: true, timeout: 10000 }
			);
		},

		/* Existing API calls */
		getSelectedRole() {
			let companyId = this.companyId;
			let url = companyId ? `web/company/${companyId}/roles` : `web/company/roles`;

			axiosIns
				.get(url)
				.then((response) => {
					this.companyRoles = response.data.companyRoles;
				})
				.catch(() => {});
		},

		getState(event) {
			axiosIns
				.get(`web/get-state-list?country_id=${event.id}`)
				.then((response) => {
					this.stateList = response.data.state_list;
				})
				.catch(() => {});
		},

		getCountry() {
			axiosIns
				.get(`web/get-country-list`)
				.then((response) => {
					this.countryList = response.data.country_list;
				})
				.catch(() => {});
		},

		getCity(event) {
			axiosIns
				.get(`web/get-city-list?state_id=${event.id}`)
				.then((response) => {
					this.cityList = response.data.city_list;
				})
				.catch(() => {});
		},

		addFarm() {
			this.$refs.simpleRules.validate().then((success) => {
				if (!success) return;

				if (!this.farmData.center_lat || !this.farmData.center_lng) {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "AlertTriangleIcon",
							variant: "danger",
							text: "Please select farm location (search/click/current location) before submitting.",
						},
					});
					return;
				}

				let formData = new FormData();
				let form_data = this.objectToFormData(this.farmData, formData);

				let companyId = this.companyId;
				let url = companyId ? `web/company/${companyId}/add-farm` : `web/add-farm`;

				axiosIns
					.post(url, form_data)
					.then((res) => {
						const data = res.data.message;
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: { icon: "CoffeeIcon", variant: "success", text: data },
						});
						shedUpdate.$emit("shedUpdateEvent", companyId);
						this.$router.push({ path: `/apps/farm/list` });
					})
					.catch((error) => {
						const data = error.response?.data?.errors || {};
						let arr = Object.values(data).flat().toString(",");
						this.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: { icon: "CoffeeIcon", variant: "danger", text: arr || "Something went wrong" },
						});
					});
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.map-wrap {
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 10px;
	overflow: hidden;
}
.farm-map {
	height: 420px;
	width: 100%;
}

/* Nominatim search dropdown */
.nominatim-wrap {
	position: relative;
}
.nominatim-list {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	z-index: 9999;
	background: #fff;
	border: 1px solid rgba(0,0,0,.12);
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0,0,0,.12);
	max-height: 240px;
	overflow-y: auto;
	margin: 2px 0 0;
	padding: 4px 0;
}
.nominatim-item {
	padding: 8px 12px;
	font-size: 0.875rem;
	cursor: pointer;
	line-height: 1.4;
	&:hover { background: #f0f5ff; }
}
.nominatim-status {
	font-size: 0.8rem;
	padding: 4px 0;
}

/* Keep your existing imports */
@import "@core/scss/vue/pages/nav-role.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>